<script setup lang="ts">
import axios from 'axios'

import CampaignEmailFirstSendModal from '../CampaignEmailFirstSendModal.vue'

import { useContentScopeEntry } from '@/composables/contentScope'

import type { FirstSendVariant } from '../CampaignEmailFirstSendModal.vue'

const { t } = useI18n()
const router = useRouter()
const notification = useNotification()
const { notifyCampaignError } = useEmailDomainGuard()

const {
  count,
  isLoadingCount,
  name,
  scope,
  step,
  emailContentId,
  conversionDelay,
  launchChoice,
  dateCampaign,
  timeCampaign,
  selectedAudience,
  emailContentSubject,
  emailContentPreviewText,
  userStoresAudience,
  userStoresCount,
  steppers,
  steps,
  selectedTemplateName,
  buildLaunchDate,
  fetchAllTemplates,
  fetchUserStoresAudience
} = useCampaignEmailForm()

const { hasRequiredMandate, hasEmailMandate, canLaunchEmailCampaign, fetchMandateStatus } = useMandate()
const { emailSettings, getEmailSettings } = useEmailSettings()

const senderDomain = computed(() => emailSettings.value?.sender_domain)
const senderDomainVerified = computed(() => senderDomain.value?.is_verified ?? true)

const processing = ref(false)
const savingDraft = ref(false)
const isFirstSendModalOpen = ref(false)
const { isScopeModalOpen, confirmScope, askScope } = useContentScopeEntry(scope)

const confirmFirstSend = () => {
  isFirstSendModalOpen.value = false
  askScope()
}

const firstSendVariant = ref<FirstSendVariant>('first')

const checkFirstCampaign = async () => {
  try {
    const { data } = await axios.get('campaigns', {
      params: { type: 'email', status: ['sent'], per_page: 1 }
    })

    firstSendVariant.value = data.meta.total === 0 ? 'first' : 'second'
    isFirstSendModalOpen.value = data.meta.total <= 1
  } catch {
    isFirstSendModalOpen.value = false
  }
}

const leaveFirstSend = () => {
  router.push({ name: 'CampaignEmailListPage' })
}

const buildParams = (isDraft: boolean) => {
  const storeIds = selectedAudience.value.stores.map((s) => String(s.value))

  return {
    type: 'email',
    name: name.value,
    scope: scope.value,
    emailContentId: emailContentId.value,
    launchDate: buildLaunchDate(),
    isStoreSenderName: false,
    hasQrcode: false,
    conversionDelay: conversionDelay.value,
    isDraft,
    audience: {
      storeIds,
      segmentIds: selectedAudience.value.lists,
      excludedSegmentIds: selectedAudience.value.excludedLists
    }
  }
}

const createCampaign = async () => {
  if (processing.value || savingDraft.value || !canLaunchEmailCampaign.value) return
  processing.value = true

  try {
    const response = await axios.post('campaigns', buildParams(false))
    notification.create('success', {
      text: t('pages.campaignEmail.create.notifications.success'),
      timeout: 5000
    })
    router.push({
      name: 'CampaignDetail',
      params: { campaignId: response.data.data.id },
      query: { type: 'email' }
    })
  } catch (error: any) {
    notifyCampaignError(error, t('pages.campaignEmail.create.notifications.error'))
  } finally {
    processing.value = false
  }
}

const saveDraft = async () => {
  if (savingDraft.value || processing.value) return
  savingDraft.value = true

  try {
    const response = await axios.post('campaigns', buildParams(true))
    notification.create('success', {
      text: t('pages.campaignEmail.create.notifications.draftSuccess'),
      timeout: 5000
    })
    router.push({
      name: 'CampaignEmailEditPage',
      params: { campaignEmailId: response.data.data.id }
    })
  } catch (error: any) {
    notification.create('error', {
      text: error?.response?.data?.message || t('pages.campaignEmail.create.notifications.draftError'),
      persist: true
    })
  } finally {
    savingDraft.value = false
  }
}

const canSaveDraft = computed(() => name.value.trim().length > 0 && !processing.value && !savingDraft.value)

onMounted(async () => {
  await Promise.all([
    fetchUserStoresAudience(),
    fetchAllTemplates(),
    fetchMandateStatus(),
    checkFirstCampaign(),
    getEmailSettings()
  ])
  if (!isFirstSendModalOpen.value) askScope()
})
</script>

<template>
  <div>
    <TheDetailHeader
      :title="t('pages.campaignEmail.create.title')"
      :parent="{ label: t('pages.campaignEmail.title'), to: { name: 'CampaignEmailListPage' } }"
    >
      <template #actions>
        <Button variant="outline" :disabled="!canSaveDraft" @click="saveDraft">
          <Spinner v-if="savingDraft" class="mr-2" />
          {{ t('pages.campaignEmail.create.validation.saveAsDraft') }}
        </Button>
      </template>
    </TheDetailHeader>

    <div class="mb-12 hidden px-4 sm:block sm:px-12">
      <BaseStepper v-model="step" :steps="steps" />
    </div>

    <component
      :is="steppers[step]"
      :key="step"
      v-model:step="step"
      v-model:count="count"
      v-model:is-loading-count="isLoadingCount"
      v-model:name="name"
      v-model:selected-audience="selectedAudience"
      v-model:email-content-id="emailContentId"
      v-model:conversion-delay="conversionDelay"
      v-model:launch-choice="launchChoice"
      v-model:date-campaign="dateCampaign"
      v-model:time-campaign="timeCampaign"
      :user-stores-audience="userStoresAudience"
      :user-stores-count="userStoresCount"
      :selected-template-name="selectedTemplateName"
      :email-subject="emailContentSubject"
      :email-preview-text="emailContentPreviewText"
      :is-processing="processing"
      :is-saving-draft="savingDraft"
      :has-required-mandate="hasRequiredMandate"
      :has-email-mandate="hasEmailMandate"
      :can-launch-email-campaign="canLaunchEmailCampaign"
      :sender-domain-verified="senderDomainVerified"
      :sender-domain-id="senderDomain?.id ?? null"
      @on-create="createCampaign"
      @on-save-draft="saveDraft"
    />

    <CampaignEmailFirstSendModal
      :is-open="isFirstSendModalOpen"
      :variant="firstSendVariant"
      @confirm="confirmFirstSend"
      @close="leaveFirstSend"
    />

    <ContentScopeModal
      v-model:open="isScopeModalOpen"
      subject="campaign"
      @confirm="confirmScope"
      @cancel="leaveFirstSend"
    />
  </div>
</template>
